/**
 * FeaturedProductsSection — section kind `featured_products`.
 *
 * Photo-forward product showcase. The storefront dispatcher resolves items
 * (sifly + dealer catalog) and precomputes each card's destination (`href`)
 * and formatted price (`priceLabel`) so the props are fully serializable —
 * shared-ui never imports from the storefront app, and the section can sit
 * behind a client boundary (the analytics wrapper) without passing functions
 * across it.
 *
 * Collapse-on-empty: with zero items the whole section (heading
 * included) returns null — no orphan heading, no empty framed box.
 *
 * One click model: the whole card is the link; the emerald CTA button
 * is the labeled affordance for that same destination. `onItemClick` is an
 * optional, non-preventing analytics hook (wired by AnalyticsFeaturedProducts)
 * — same pattern as ContactCtaSection's `onCtaClick`.
 */

import Image from 'next/image'
import Link from 'next/link'
import { SaleBadge, SalePriceLabel } from '../product/CardPrice'

export type FeaturedProductCardView = {
  source: 'sifly' | 'dealer'
  slug: string
  name: string
  image: string | null
  cta: 'view_product' | 'book_demo' | 'request_quote'
  /** Precomputed destination (PDP, FareHarbor, or contact/quote path). */
  href: string
  /** Precomputed, currency-formatted price; null when prices are hidden or unknown. */
  priceLabel: string | null
  /** True when the effective price is a live sale — drives the badge + red price. */
  onSale?: boolean
  /** Precomputed, currency-formatted compare-at (struck through); null when not on sale. */
  compareAtLabel?: string | null
}

type Props = {
  heading?: string
  items: FeaturedProductCardView[]
  onItemClick?: (item: FeaturedProductCardView) => void
}

const CTA_LABELS: Record<FeaturedProductCardView['cta'], string> = {
  view_product: 'View product',
  book_demo: 'Book a demo',
  request_quote: 'Request a quote',
}

// Photo-forward, anti-slop layout. The grid template adapts to count so a
// single feature reads as a hero (constrained width, two-up max) rather
// than a lonely third of a rigid 3-up row; 3-4 items flow into a
// responsive grid that collapses to a single column on mobile.
function gridClassFor(count: number): string {
  if (count <= 1) return 'grid-cols-1 max-w-2xl'
  if (count === 2) return 'grid-cols-1 sm:grid-cols-2'
  return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
}

export function FeaturedProductsSection({ heading, items, onItemClick }: Props) {
  // Collapse-on-empty — never render an empty frame or orphan heading.
  if (items.length === 0) return null

  return (
    <section
      className="max-w-[var(--section-max-width,var(--max-width-page))] mx-auto px-[var(--section-padding-x,1rem)] py-[var(--space-section)]"
      data-component="FeaturedProductsSection"
    >
      {heading && (
        <h2
          className="text-2xl md:text-3xl font-bold text-[var(--color-text-primary)] mb-8 text-center"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          {heading}
        </h2>
      )}
      <div className={`grid ${gridClassFor(items.length)} gap-6 mx-auto`}>
        {items.map((item) => (
          <Link
            key={`${item.source}:${item.slug}`}
            href={item.href}
            onClick={onItemClick ? () => onItemClick(item) : undefined}
            className="group flex flex-col rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-primary)] overflow-hidden hover:shadow-[var(--shadow-md)] transition-shadow"
          >
            <div className="relative aspect-[4/3] bg-[var(--color-bg-secondary)]">
              {item.image && (
                <Image
                  src={item.image}
                  alt={item.name}
                  fill
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  className="object-cover"
                />
              )}
              {item.onSale && <SaleBadge className="absolute left-3 top-3" />}
            </div>
            <div className="flex flex-1 flex-col p-5">
              <h3
                className="text-lg font-semibold text-[var(--color-text-primary)] group-hover:text-[var(--color-cta-readable)] transition-colors"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {item.name}
              </h3>
              {item.priceLabel != null && (
                <SalePriceLabel
                  priceLabel={item.priceLabel}
                  compareAtLabel={item.compareAtLabel}
                  onSale={item.onSale}
                  className="mt-1 text-sm text-[var(--color-text-secondary)]"
                />
              )}
              <span
                className="mt-4 inline-flex items-center justify-center self-start px-5 h-11 rounded-md bg-[var(--color-cta)] text-[var(--color-cta-text)] text-sm font-medium group-hover:opacity-90 transition-opacity"
              >
                {CTA_LABELS[item.cta]}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  )
}
